import React from "react";
import { ArrowUpRight, Radio, Network, SignalHigh, TowerControl, Antenna, ShieldCheck } from "lucide-react";
import SectionLabel from "./brand/SectionLabel";

const TOWER_IMG = "/brand/towers.jpg?v=5";
const NET_IMG =
  "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?auto=format&fit=crop&w=1600&q=80";
const DAS_IMG = "/brand/das-systems.jpg?v=2";
const WIRELESS_IMG = "/brand/wireless-links.jpg?v=3";
const BOOSTER_IMG = "/brand/cell-boosters.jpg?v=10";
const SECURITY_IMG = "/brand/security.jpg?v=9";

const SERVICES = [
  {
    id: "wireless",
    num: "01",
    icon: Radio,
    title: "Wireless Links",
    subtitle: "Point-to-Point · Point-to-Multipoint",
    description:
      "Rural high-speed internet and high-throughput wireless backhaul for farms, acreages, and solar sites. We design, install, and align links that hold up against Alberta distance and weather.",
    bullets: [
      "Licensed + unlicensed PtP / PtMP",
      "Rural high-speed internet infrastructure",
      "Farm & acreage connectivity solutions",
      "Multi-gigabit 60 GHz / mmWave links",
      "Solar / off-grid site comms",
      "Backhaul for cameras, SCADA & IoT",
      "RF path planning & alignment",
    ],
    image: WIRELESS_IMG,
    valueLabel: "Rural + Industrial Grade",
    span: "lg:col-span-12",
    featured: true,
  },
  {
    id: "networking",
    num: "02",
    icon: Network,
    title: "Home & Business Networking",
    subtitle: "Ubiquiti UniFi Full-Stack",
    description:
      "End-to-end wired and wireless networks — primarily built on Ubiquiti UniFi, with other enterprise platforms deployed where they fit the job. From gateways and PoE switches to ceiling APs, mesh, VLANs, and guest networks. Reliable infrastructure, cleanly installed and documented.",
    bullets: [
      "Business-grade wireless networking",
      "Complete UniFi deployments",
      "Clean rack & network installs",
      "Remote network management",
      "Structured network cabling",
      "Custom system design",
    ],
    image: NET_IMG,
    valueLabel: "Prosumer → Enterprise",
    span: "lg:col-span-4",
  },
  {
    id: "security",
    num: "03",
    icon: ShieldCheck,
    title: "Security & Access Control",
    subtitle: "Cameras · Door Access · Sensors · Intrusion",
    description:
      "Integrated building security on the Ubiquiti UniFi platform — Protect cameras with on-prem NVR and cloud remote viewing, UniFi Access for keyless door entry and intercom, plus intrusion and environmental sensors. Hikvision installs available where it fits the job.",
    bullets: [
      "Smart building access control",
      "On-prem NVR with cloud access",
      "UniFi intercom & door viewer systems",
      "Intrusion and environmental sensors",
    ],
    image: SECURITY_IMG,
    valueLabel: "Eyes, Locks & Alerts",
    span: "lg:col-span-4",
    imgAspect: "aspect-[1800/1330]",
  },
  {
    id: "das",
    num: "04",
    icon: Antenna,
    title: "Cellular DAS",
    subtitle: "Business · Commercial · Enterprise",
    description:
      "Distributed Antenna Systems for multi-floor offices, commercial buildings, and enterprise facilities. We survey, design, install, validate, and coordinate with carriers. Epicom is a Nextivity Certified Partner with CEL-FI Enterprise Sales & Installation certification.",
    bullets: [
      "Passive & active DAS builds",
      "Single-carrier & multi-carrier",
      "iBwave / in-building design",
      "Carrier coordination & compliance",
    ],
    image: DAS_IMG,
    valueLabel: "Enterprise-Grade Coverage",
    span: "lg:col-span-4",
  },
  {
    id: "towers",
    num: "05",
    icon: TowerControl,
    title: "Small & Medium Towers",
    subtitle: "Self-Supporting · Right-Sized",
    description:
      "Small and medium self-supporting tower installs — right-sized structures for rural and light-commercial jobs. We also turn existing structures (grain legs, silos, outbuildings) into practical comms towers.",
    bullets: [
      "Self-supporting tower installs",
      "Professional tower cabling & grounding",
      "Grain leg / silo conversions",
      "Foundation, anchor & safety work",
    ],
    image: TOWER_IMG,
    valueLabel: "Practical, Not Oversized",
    span: "lg:col-span-6",
  },
  {
    id: "cell-boosters",
    num: "06",
    icon: SignalHigh,
    title: "Cell Boosters",
    subtitle: "Residential · Commercial · Industrial",
    description:
      "Cell signal boosters for properties where coverage falls off. Site surveys, antenna placement, and donor-signal tuning so your calls and data actually stay up. Epicom is a SureCall Certified Installer and a WilsonPro Certified Installer / Integrator.",
    bullets: [
      "Home & acreage installs",
      "Vehicle & RV boosters",
      "Industrial & oilfield signal solutions",
      "Signal surveys & tuning",
    ],
    image: BOOSTER_IMG,
    valueLabel: "Signal Where There Isn't Any",
    span: "lg:col-span-6",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-12 md:py-20 bg-bg-primary"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Section header */}
        <div className="grid md:grid-cols-12 gap-8 mb-16">
          <div className="md:col-span-4">
            <SectionLabel testid="services-overline" className="mb-5">
              Capabilities
            </SectionLabel>
            <h2 className="heading text-4xl md:text-5xl lg:text-6xl text-white uppercase leading-[0.95]">
              What We <br /> <span className="text-signal">Build.</span>
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6 flex items-end">
            <p className="text-steel-light text-base md:text-lg leading-relaxed">
              Epicom Systems Ltd. contracts communications and security
              infrastructure for farms, businesses and homes across Southern
              Alberta — from wireless backhaul and full networks, to cell
              boosters and right-sized tower installs, to enterprise DAS and
              integrated security. One team, one number, from survey to
              sign-off.
            </p>
          </div>
        </div>

        {/* Service grid — image substrate revealed in the gaps + outer frame */}
        <div className="relative">
          {/* Substrate image (extends ABOVE/BELOW the grid into adjacent dead space) */}
          <div
            aria-hidden="true"
            className="absolute left-0 right-0 top-0 md:-top-2 lg:-top-6 -bottom-3 md:-bottom-6 lg:-bottom-12 bg-cover bg-center"
            style={{ backgroundImage: "url('/brand/services-bg.jpg?v=1')" }}
          />
          {/* Dark veil so the image reads as texture, not a competing photo */}
          <div
            aria-hidden="true"
            className="absolute left-0 right-0 top-0 md:-top-2 lg:-top-6 -bottom-3 md:-bottom-6 lg:-bottom-12 bg-black/55"
          />
          {/* Subtle vignette for polish */}
          <div
            aria-hidden="true"
            className="absolute left-0 right-0 top-0 md:-top-2 lg:-top-6 -bottom-3 md:-bottom-6 lg:-bottom-12"
            style={{
              background:
                "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.5) 100%)",
            }}
          />

          <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 items-start gap-2 md:gap-2.5 p-6 md:p-10 lg:p-12">
          {SERVICES.map((s) => {
            const Icon = s.icon;
            if (s.featured) {
              // Wide hero card: image LEFT (large), text RIGHT
              return (
                <article
                  key={s.id}
                  id={`service-${s.id}`}
                  data-testid={`service-card-${s.id}`}
                  className={`group relative overflow-hidden scroll-mt-24 ${s.span}`}
                >
                  <div className="grid lg:grid-cols-12">
                    <div className="lg:col-span-5 relative aspect-[4/5] lg:aspect-auto lg:min-h-[640px] overflow-hidden bg-black">
                      <img
                        src={s.image}
                        alt={`${s.title} — ${s.subtitle}`}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-cover object-center grayscale group-hover:grayscale-0 transition-all duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-bg-secondary/40 hidden lg:block" />
                      <div className="absolute inset-0 bg-gradient-to-t from-bg-secondary/80 via-transparent to-transparent lg:hidden" />
                      <div className="absolute top-6 left-6 flex items-center gap-3">
                        <span className="font-mono text-xs tracking-[0.25em] text-signal bg-bg-primary/70 backdrop-blur px-3 py-1.5 border border-signal/40">
                          {s.num} · FEATURED
                        </span>
                      </div>
                      <div className="absolute bottom-6 right-6 text-signal opacity-0 group-hover:opacity-100 transition-opacity">
                        <ArrowUpRight size={32} strokeWidth={2} />
                      </div>
                    </div>

                    <div className="lg:col-span-7 p-8 md:p-10 lg:p-12 flex flex-col justify-center bg-bg-secondary group-hover:bg-[#16181B] transition-colors">
                      <div className="flex items-center justify-between mb-6">
                        <Icon className="text-signal" size={36} strokeWidth={1.5} />
                        <span className="font-mono text-[10px] tracking-[0.25em] text-steel-light uppercase">
                          {s.valueLabel}
                        </span>
                      </div>
                      <h3 className="heading text-3xl md:text-4xl lg:text-5xl text-white uppercase mb-2 leading-tight">
                        {s.title}
                      </h3>
                      <div className="font-mono text-xs tracking-[0.2em] text-steel-light uppercase mb-5">
                        {s.subtitle}
                      </div>
                      <p className="text-steel-light text-base leading-relaxed mb-6">
                        {s.description}
                      </p>
                      <ul className="space-y-2">
                        {s.bullets.map((b) => (
                          <li
                            key={b}
                            className="flex items-start gap-3 text-sm text-white/80"
                          >
                            <span className="text-signal mt-[6px] w-2 h-[2px] bg-signal inline-block flex-shrink-0" />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </article>
              );
            }

            // Standard card: image on TOP (wide), text below
            return (
              <article
                key={s.id}
                id={`service-${s.id}`}
                data-testid={`service-card-${s.id}`}
                className={`group relative flex flex-col overflow-hidden self-start scroll-mt-24 ${s.span}`}
              >
                <div className={`relative ${s.imgAspect || "aspect-[16/10]"} overflow-hidden bg-black`}>
                  <img
                    src={s.image}
                    alt={`${s.title} — ${s.subtitle}`}
                    loading="lazy"
                    decoding="async"
                    className={`absolute inset-0 w-full h-full object-cover ${s.imgClass || ""} grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-bg-secondary/80 via-bg-secondary/20 to-transparent" />

                  <div className="absolute top-5 left-5 flex items-center gap-3">
                    <span className="font-mono text-xs tracking-[0.25em] text-signal bg-bg-primary/70 backdrop-blur px-3 py-1.5 border border-signal/40">
                      {s.num}
                    </span>
                  </div>
                  <div className="absolute top-5 right-5 font-mono text-[10px] tracking-[0.25em] text-white/80 uppercase bg-bg-primary/60 backdrop-blur px-3 py-1.5">
                    {s.valueLabel}
                  </div>
                  <div className="absolute bottom-5 right-5 text-signal opacity-0 group-hover:opacity-100 transition-opacity">
                    <ArrowUpRight size={28} strokeWidth={2} />
                  </div>
                </div>

                <div className="p-8 md:p-10 bg-bg-secondary group-hover:bg-[#16181B] transition-colors">
                  <Icon
                    className="text-signal mb-5 group-hover:scale-110 transition-transform origin-left"
                    size={32}
                    strokeWidth={1.5}
                  />
                  <h3 className="heading text-2xl md:text-3xl text-white uppercase mb-2 leading-tight">
                    {s.title}
                  </h3>
                  <div className="font-mono text-xs tracking-[0.2em] text-steel-light uppercase mb-5">
                    {s.subtitle}
                  </div>
                  <p className="text-steel-light text-sm md:text-base leading-relaxed mb-6">
                    {s.description}
                  </p>
                  <ul className="space-y-2">
                    {s.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-3 text-sm text-white/80"
                      >
                        <span className="text-signal mt-[6px] w-2 h-[2px] bg-signal inline-block flex-shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            );
          })}
          </div>
        </div>
      </div>
    </section>
  );
}
